import {
	buildBreadcrumbManager,
	buildQuerySummary,
	buildResultList,
	buildSearchParameterManager,
	loadAdvancedSearchQueryActions,
	loadSearchAnalyticsActions
} from "@coveo/headless";
import { useEffect, useMemo, useState } from "react";
import FacetGroup from "components/coveo-headless/components/FacetGroup";
import FacetGroupMobile from "components/coveo-headless/components/FacetGroupMobile";
import Facet from "components/coveo-headless/components/Facet";
import { generateFacets, generateTabs } from "components/coveo-headless/controllers/Controllers";
import serializeUrl from "components/coveo-headless/utils/serialize-url";
import deserializeUrl from "components/coveo-headless/utils/deserialize-url";
import BreadcrumbManager from "components/coveo-headless/components/BreadcrumbManager";
import ContentCards from "components/coveo-headless/ContentCards";
import InsightsTab from "./InsightsTab";
import InsightsTabMobile from "./InsightsTabMobile";
import TotalResults from "components/coveo-headless/components/TotalResults";
import { ImageFilter } from "components/utility/images";
import StickyHeader from "components/utility/sticky-header/StickyHeader";
import StickyHeaderContainer from "components/utility/sticky-header/StickyHeaderContainer";
import { setBodyPositionFixed } from "utils/dom";
import {
	registerField,
	registerSearchHub,
	launchAnalytics
} from "components/coveo-headless/utils/initAnalytics";
import InsightsLoader from "components/coveo-headless/coveo-loaders/InsightsLoader/InsightsLoader";

const desktopWidth = 767;

const Insights = ({ engine, jsonData, pageType, searchHub, templates }) => {
	const {
		aq,
		context,
		fields,
		filters,
		showMoreText,
		showResults,
		tabs,
		resultDescription,
		facetQuantifier
	} = jsonData;
	const { facetName } = filters;

	const facetGroupClasses = {
		container: "insights-browse-options-container",
		facetParent: "insights-browse-options-list t-body-sm "
	};

	const mobileFacetGroupLabel = "filter";
	// grab default sort for url
	const { criteria } = filters.sortCriteria.find((item) => item.isActive === true);

	/*
	 * coveo-headless docs say:
	 * Search parameters, defined in the url's hash, should not be restored until all components are registered.
	 * Additionally, a search should not be executed until search parameters are restored.
	 *
	 * This means all tabs/facets/queries/etc that use a "build" method from
	 * the @coveo/headless package MUST run before executing any search.
	 * Saving parameters in a state does NOT immediately update the engine, resulting in odd side effects
	 * (e.g., facets missing on screen, wrong results).
	 * useMemo initializes all parameters on page load
	 * and immediately changes a parameter upon a search execution
	 */
	const tabsMemo = useMemo(() => generateTabs(engine, tabs), [engine, tabs]);
	const facetsMemo = useMemo(() => generateFacets(engine, facetName), [engine, facetName]);
	const breadcrumbs = useMemo(() => buildBreadcrumbManager(engine), [engine]);
	const querySummary = useMemo(() => buildQuerySummary(engine), [engine]);
	const resultList = useMemo(() => buildResultList(engine), [engine]);
	const [mobileActiveTab, setMobileActiveTab] = useState("");
	const [toggleMobileTab, setToggleMobileTab] = useState(false);
	const [toggleMobileFacetGroup, setToggleMobileFacetGroup] = useState(false);
	const [toggleMobileFacetList, setToggleMobileFacetList] = useState(false);
	const [mobileFilterCount, setMobileFilterCount] = useState(0);
	const [mobileSelectedFacets, setMobileSelectedFacets] = useState({});
	const [selectedFacet, setSelectedFacet] = useState(0);
	const [toggleFacets, setToggleFacets] = useState(false);
	const [loading, setIsLoading] = useState(true);
	const [loaderState, setLoaderState] = useState({});

	const closeMobileFilters = () => {
		if (window.innerWidth >= desktopWidth) {
			if (document.body.style.position) {
				document.body.style.position = null;
			}
			setToggleMobileFacetGroup(false);
			setToggleMobileTab(false);
		}
	};

	useEffect(() => {
		window.addEventListener("resize", closeMobileFilters);
		return () => {
			window.removeEventListener("resize", closeMobileFilters);
		};
	}, []);

	useEffect(() => {
		resultList.subscribe(() => {
			setLoaderState(resultList.state);
		});
	}, [resultList]);

	useEffect(() => {
		if (loaderState?.isLoading === false) {
			setIsLoading(false);
		}
	}, [loaderState]);
	useEffect(() => {
		const hash = window.location.hash.slice(1);
		let parameters = deserializeUrl(aq, criteria, [], tabs, hash);
		/** when landing on insights from [url]/insights, (i.e. a click from the nav)
			there are no parameters and the above var will be an empty object.
			Add parameters so the engine can filter results on page load if initial url is [url]/insights.
			Parameters should be:
			category: whatever category has the `isActive` flag in the Tabs object, sort: @publishdate descending
		*/
		if (Object.keys(parameters).length === 0) {
			const defaultActiveTab = Object.entries(tabs)
				.find(([, value]) => value.isActive)
				.flat();
			if (defaultActiveTab.length) {
				parameters.aq = defaultActiveTab[1].tabFacet + aq;
				parameters.tab = defaultActiveTab[0];
			} else {
				const firstTab = Object.keys(tabs)[0];
				parameters.aq = tabs[firstTab].tabFacet + aq;
				parameters.tab = firstTab;
			}
		}
		// on page load, set active tab for mobile
		setMobileActiveTab(parameters.tab);
		const searchParameterManager = buildSearchParameterManager(engine, {
			initialState: {
				parameters: {
					aq: parameters.aq,
					tab: parameters.tab,
					f: parameters.f
				}
			}
		});
		// when user filters via UI, update url hash
		searchParameterManager.subscribe(() => {
			const url = serializeUrl(searchParameterManager.state.parameters, criteria, pageType);
			// set active facets and active facet count for mobile
			if (searchParameterManager.state.parameters.f) {
				const newMobileFilterCount = Object.keys(searchParameterManager.state.parameters.f).reduce(
					(acc, val) => {
						acc += searchParameterManager.state.parameters.f[val].length;
						return acc;
					},
					0
				);
				setMobileFilterCount(newMobileFilterCount);
				setMobileSelectedFacets(searchParameterManager.state.parameters.f);
			} else {
				setMobileFilterCount(0);
				setMobileSelectedFacets({});
			}
			window.history.pushState(null, document.title, url);
		});

		/**
		 * when user types into url bar, deserialize new parameters, update UI
		 * update searchParameterManager with new query parameters
		 */
		const onHashChange = () => {
			const hash = window.location.hash.slice(1);
			const parameters = deserializeUrl(aq, criteria, [], tabs, decodeURIComponent(hash));
			searchParameterManager.synchronize(parameters);
		};

		const { logInterfaceLoad } = loadSearchAnalyticsActions(engine);
		const { updateAdvancedSearchQueries } = loadAdvancedSearchQueryActions(engine);

		engine.dispatch(updateAdvancedSearchQueries({ aq: parameters.aq }));
		// this runs any searches onload of the page; is necessary, otherwise there is no aq or searchParameterManager start
		engine.executeFirstSearch(logInterfaceLoad());
		window.addEventListener("hashchange", onHashChange);
		return () => {
			window.removeEventListener("hashchange", onHashChange);
		};
		// adding all dependencies react recommends cause coveo to go into an infinite loop
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [aq, engine, filters.sortCriteria, tabs]);

	launchAnalytics(engine, context);
	registerField(engine, fields);
	registerSearchHub(engine, searchHub);

	if (loading) return <InsightsLoader numberOfCards={Number(jsonData.filters.numberOfResults)} />;
	return (
		<>
			<div
				className={`body-mask-target ${toggleMobileTab || toggleMobileFacetGroup ? "body-mask" : ""}`}
				role="button"
				tabIndex={0}
				onClick={() => {
					setBodyPositionFixed();
					setToggleMobileFacetGroup(false);
					setToggleMobileTab(false);
				}}
				onKeyDown={() => {
					setBodyPositionFixed();
					setToggleMobileFacetGroup(false);
					setToggleMobileTab(false);
				}}
			></div>
			<StickyHeaderContainer>
				<StickyHeader>
					<section className="insights-browse-bar-container container">
						<div className="insights-browse-bar-desktop">
							<div className="insights-browse-bar-tabs">
								{Object.keys(tabsMemo).map((tab) => (
									<InsightsTab
										key={tab}
										id={tab}
										controller={tabsMemo[tab]}
										engine={engine}
										aq={aq}
										tabFacet={tabs[tab].tabFacet}
									>
										{tab}
									</InsightsTab>
								))}
							</div>
							<FacetGroup
								classes={facetGroupClasses}
								facets={facetsMemo}
								fields={facetName}
								page={pageType}
								toggle={toggleFacets}
								setSelectedFacet={setSelectedFacet}
								setToggle={setToggleFacets}
							/>
							<div className={`insights-browse-options-sublist ${toggleFacets ? "" : "hide"}`}>
								<Facet facet={facetsMemo[selectedFacet]} facetGroup={fields[selectedFacet].name} />
							</div>
							<div className="insights-browse-bar-divider"></div>
						</div>
						<div className="insights-browse-bar-mobile">
							<div
								className="insights-browse-bar-mobile-dropdown"
								role="button"
								tabIndex={0}
								onClick={() => {
									setBodyPositionFixed();
									setToggleMobileTab(!toggleMobileTab);
								}}
								onKeyDown={() => {
									setBodyPositionFixed();
									setToggleMobileTab(!toggleMobileTab);
								}}
							>
								{mobileActiveTab}
							</div>
							<div className="insights-browse-bar-mobile-header">
								Filter
								<div
									className="insights-browse-bar-mobile-filter"
									role="button"
									tabIndex={0}
									onClick={() => {
										setBodyPositionFixed();
										setToggleMobileFacetGroup(!toggleMobileFacetGroup);
									}}
									onKeyDown={() => {
										setBodyPositionFixed();
										setToggleMobileFacetGroup(!toggleMobileFacetGroup);
									}}
								>
									<ImageFilter />
									<div className="insights-browse-bar-mobile-filter-counter">
										{mobileFilterCount === 0 ? "" : mobileFilterCount}
									</div>
								</div>
							</div>
						</div>
					</section>
				</StickyHeader>
			</StickyHeaderContainer>
			<div className="container">
				{toggleMobileTab && (
					<div
						className={`coveo-headless-mobile-tab-section ${
							toggleMobileTab ? "coveo-headless-mobile-tab-section-show" : ""
						}`}
					>
						{Object.keys(tabsMemo).map((tab) => (
							<InsightsTabMobile
								key={tab}
								id={tab}
								controller={tabsMemo[tab]}
								engine={engine}
								aq={aq}
								tabFacet={tabs[tab].tabFacet}
								setMobileActiveTab={setMobileActiveTab}
							>
								{tab}
							</InsightsTabMobile>
						))}
					</div>
				)}
				{toggleMobileFacetGroup && (
					<div
						className={`coveo-headless-mobile-facet-group ${
							toggleMobileFacetGroup ? "coveo-headless-mobile-facet-group-show" : ""
						} ${toggleMobileFacetList ? "coveo-headless-mobile-facet-group-list-open" : ""}`}
					>
						<FacetGroupMobile
							breadcrumbs={breadcrumbs}
							facets={facetsMemo}
							fields={facetName}
							mobileFacetGroupLabel={mobileFacetGroupLabel}
							mobileFilterCount={mobileFilterCount}
							mobileSelectedFacets={mobileSelectedFacets}
							pageType={pageType}
							toggleMobileFacetList={toggleMobileFacetList}
							setToggleMobileFacetGroup={setToggleMobileFacetGroup}
							setToggleMobileFacetList={setToggleMobileFacetList}
							facetQuantifier={facetQuantifier}
						>
							{showResults}
						</FacetGroupMobile>
					</div>
				)}
				<div className="insights-breadcrumbs-container grid">
					<TotalResults
						controller={querySummary}
						showInFilters={false}
						pageType={pageType}
						totalResultsDescription={resultDescription}
					/>
					<BreadcrumbManager controller={breadcrumbs} page={pageType} />
				</div>
				<div className="coveo-headless-content-cards-wrapper">
					<ContentCards
						controller={resultList}
						headlessEngine={engine}
						showMoreText={showMoreText}
						templates={templates}
					/>
				</div>
			</div>
		</>
	);
};

export default Insights;
